import { motion } from "framer-motion";
import { ArrowClockwise, WarningCircle, CheckCircle } from "@phosphor-icons/react";

export function DriftAnalysisSection({ driftData, driftLoading, driftTrend, correctingAssetId, onRefresh, onCorrect }) {
  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.22 }}
        className="bg-[var(--bg-card)] border border-[var(--border)] rounded-lg p-5"
        data-testid="drift-summary"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-[var(--text-primary)]">Drift Analysis</h2>
          <button
            onClick={onRefresh}
            className="text-xs text-[var(--text-muted)] hover:text-[var(--text-primary)] flex items-center gap-1"
            data-testid="drift-refresh-btn"
          >
            <ArrowClockwise size={12} />
            Refresh
          </button>
        </div>
        {driftLoading ? (
          <p className="text-xs text-[var(--text-muted)]">Checking consistency across assets and channels...</p>
        ) : driftData ? (
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="rounded border border-emerald-500/20 bg-emerald-500/10 p-2">
                <p className="text-xs text-emerald-300">Green</p>
                <p className="text-lg font-['JetBrains_Mono']">{driftData.summary?.green || 0}</p>
              </div>
              <div className="rounded border border-yellow-500/20 bg-yellow-500/10 p-2">
                <p className="text-xs text-yellow-300">Yellow</p>
                <p className="text-lg font-['JetBrains_Mono']">{driftData.summary?.yellow || 0}</p>
              </div>
              <div className="rounded border border-red-500/20 bg-red-500/10 p-2">
                <p className="text-xs text-red-300">Red</p>
                <p className="text-lg font-['JetBrains_Mono']">{driftData.summary?.red || 0}</p>
              </div>
            </div>
            <div className="space-y-2">
              {(driftData.assets || []).slice(0, 5).map((asset) => (
                <div key={asset.asset_id} className="rounded border border-[var(--border)] p-3" data-testid={`drift-asset-${asset.asset_id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <p className="text-sm text-[var(--text-primary)]">{asset.title}</p>
                      <p className="text-[10px] text-[var(--text-muted)]">Overall: {asset.scores?.overall || 0}</p>
                    </div>
                    <span className={`text-[10px] uppercase px-2 py-0.5 rounded border ${
                      asset.drift_status === "green"
                        ? "text-emerald-300 border-emerald-500/20 bg-emerald-500/10"
                        : asset.drift_status === "yellow"
                          ? "text-yellow-300 border-yellow-500/20 bg-yellow-500/10"
                          : "text-red-300 border-red-500/20 bg-red-500/10"
                    }`} data-testid={`drift-status-${asset.asset_id}`}>
                      {asset.drift_status}
                    </span>
                  </div>
                  <p className="text-xs text-[var(--text-muted)] mt-1 flex items-start gap-1">
                    {asset.drift_status === "green" ? <CheckCircle size={14} className="mt-0.5 shrink-0" /> : <WarningCircle size={14} className="mt-0.5 shrink-0" />}
                    {asset.reasons?.terminology_consistency}
                  </p>
                  {asset.drift_status !== "green" && (
                    <button
                      onClick={() => onCorrect(asset)}
                      disabled={correctingAssetId === asset.asset_id}
                      className="mt-2 text-xs px-2 py-1 rounded bg-[#FF4400] text-white hover:bg-[#FF6633] disabled:opacity-50"
                      data-testid={`drift-correct-${asset.asset_id}`}
                    >
                      {correctingAssetId === asset.asset_id ? "Correcting..." : "Propose correction"}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <p className="text-xs text-[var(--text-muted)]">Generate at least one asset to run drift analysis.</p>
        )}
      </motion.div>

      {/* Drift Trend Chart */}
      {driftTrend?.length > 1 && (
        <DriftTrendChart driftTrend={driftTrend} />
      )}
    </>
  );
}

function DriftTrendChart({ driftTrend }) {
  const maxScore = 100;
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.24 }}
      className="bg-[var(--bg-card)] border border-[var(--border)] rounded-lg p-5"
      data-testid="drift-trend-chart"
    >
      <h2 className="text-sm font-semibold text-[var(--text-primary)] mb-4">Drift Score Trend</h2>
      <div className="flex items-end gap-1 h-36">
        {driftTrend.map((pt, i) => {
          const avg = pt.avg_overall || 0;
          const color = avg >= 80 ? "#10B981" : avg >= 60 ? "#F59E0B" : "#EF4444";
          return (
            <div key={pt.date} className="flex-1 flex flex-col items-center gap-1 min-w-0 group relative">
              <span className="text-[9px] font-['JetBrains_Mono'] text-[var(--text-muted)]">{avg}</span>
              <motion.div
                initial={{ height: 0 }}
                animate={{ height: `${(avg / maxScore) * 100}%` }}
                transition={{ delay: 0.3 + i * 0.04, duration: 0.4 }}
                className="w-full rounded-t min-h-[2px]"
                style={{ backgroundColor: color }}
              />
              <span className="text-[8px] text-[var(--text-muted)] truncate w-full text-center">
                {new Date(pt.date + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}
              </span>
            </div>
          );
        })}
      </div>
      <div className="flex items-center justify-center gap-4 mt-3 text-[10px] text-[var(--text-muted)]">
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" />80+</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-yellow-500 inline-block" />60-79</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500 inline-block" />&lt;60</span>
      </div>
    </motion.div>
  );
}
